import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/shadcn-ui";

import { Spinner } from "../spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all no-underline hover:no-underline aria-disabled:opacity-50 aria-disabled:cursor-default [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive focus-visible:aria-invalid:ring-ring/50 focus-visible:aria-invalid:border-ring",
  {
    variants: {
      variant: {
        default:
          "bg-secondary text-secondary-foreground border border-input hover:bg-secondary/80 aria-disabled:hover:bg-secondary",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 aria-disabled:hover:bg-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 dark:aria-disabled:hover:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground aria-disabled:hover:bg-background aria-disabled:hover:text-inherit dark:bg-secondary dark:border-input dark:hover:bg-accent dark:aria-disabled:hover:bg-secondary",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-disabled:hover:bg-secondary",
        ghost:
          "hover:bg-accent hover:text-accent-foreground aria-disabled:hover:bg-transparent aria-disabled:hover:text-inherit dark:hover:bg-accent/50 dark:aria-disabled:hover:bg-transparent",
        link: "text-primary underline-offset-4 hover:underline aria-disabled:hover:no-underline",
      },
      size: {
        default: "h-8 gap-1.5 px-2.5",
        sm: "h-7 text-xs rounded-md gap-1 px-2 has-[>svg]:px-1.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];

export type ButtonSize = VariantProps<typeof buttonVariants>["size"];

function getSpinnerSize(size: ButtonSize): number {
  switch (size) {
    case "lg":
      return 25;
    case "icon":
    case "icon-lg":
      return 24;
    case "icon-sm":
      return 18;
    default:
      return 20;
  }
}

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  pending = false,
  disabled: disabledProp,
  children,
  onClick,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    pending?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  const disabled = disabledProp || pending;

  // When asChild is true, Slot expects a single child element
  if (asChild) {
    return (
      <Comp
        data-slot="button"
        data-variant={variant}
        data-size={size}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      aria-disabled={disabled} // Custom `disabled` impl to keep mouse hover events
      tabIndex={disabled ? -1 : undefined}
      onClick={disabled ? (event) => event.preventDefault() : onClick}
      className={cn("relative", buttonVariants({ variant, size, className }))}
      {...props}
    >
      <span
        className={cn(
          "inline-flex items-center gap-[inherit]",
          pending && "invisible",
        )}
      >
        {children}
      </span>

      {pending && (
        <span className="absolute left-0 top-0 w-full h-full flex items-center justify-center">
          <Spinner size={getSpinnerSize(size)} />
        </span>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
